import { Check, ChevronsUpDown, X } from 'lucide-react';
import React from 'react';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@components/ui/popover';
import { cn } from '@core/utils/cn';
import { Spinner } from '../spinner';
import { FieldError, FieldLabel } from '../textfield';

interface BaseProps<T> {
    label?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    options: readonly T[];
    getOptionLabel(option: T): string;
    getOptionKey?(option: T): string | number;
    onInputChange?(text: string): void;
    onScrollEnd?(): void;
    renderOption?(option: T): React.ReactNode;
    loading?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: string;
    helperText?: string;
    emptyText?: string;
    className?: string;
    size?: 'small' | 'medium';
}

export interface ComboboxProps<T> extends BaseProps<T> {
    value: T | null;
    onChange(value: T | null): void;
}

export interface MultiComboboxProps<T> extends BaseProps<T> {
    value: T[];
    onChange(value: T[]): void;
    renderChip?(option: T, remove: () => void): React.ReactNode;
}

function optionKey<T>(props: BaseProps<T>, option: T) {
    return String(
        props.getOptionKey
            ? props.getOptionKey(option)
            : props.getOptionLabel(option)
    );
}

function OptionList<T>({
    props,
    isSelected,
    onSelect
}: {
    props: BaseProps<T>;
    isSelected(option: T): boolean;
    onSelect(option: T): void;
}) {
    return (
        <Command shouldFilter={!props.onInputChange}>
            <CommandInput
                placeholder={props.searchPlaceholder ?? 'Search...'}
                onValueChange={props.onInputChange}
            />
            <CommandList
                onScroll={(e) => {
                    const el = e.currentTarget;
                    if (
                        props.onScrollEnd &&
                        el.scrollTop + el.clientHeight >= el.scrollHeight - 8
                    ) {
                        props.onScrollEnd();
                    }
                }}
            >
                {props.loading && (
                    <div className="flex justify-center p-2">
                        <Spinner size="sm" />
                    </div>
                )}
                <CommandEmpty>{props.emptyText ?? 'No results'}</CommandEmpty>
                <CommandGroup>
                    {props.options.map((option) => {
                        const key = optionKey(props, option);
                        return (
                            <CommandItem
                                key={key}
                                value={key}
                                keywords={[props.getOptionLabel(option)]}
                                onSelect={() => onSelect(option)}
                            >
                                <Check
                                    className={cn(
                                        'size-4',
                                        isSelected(option)
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                    )}
                                />
                                {props.renderOption
                                    ? props.renderOption(option)
                                    : props.getOptionLabel(option)}
                            </CommandItem>
                        );
                    })}
                </CommandGroup>
            </CommandList>
        </Command>
    );
}

function Shell<T>({
    props,
    children
}: {
    props: BaseProps<T>;
    children: React.ReactNode;
}) {
    return (
        <div className={cn('flex w-full flex-col gap-1.5', props.className)}>
            <FieldLabel label={props.label} required={props.required} />
            {children}
            {props.helperText && !props.error && (
                <p className="text-xs text-muted-foreground">
                    {props.helperText}
                </p>
            )}
            <FieldError error={props.error} />
        </div>
    );
}

export function Combobox<T>(props: ComboboxProps<T>) {
    const [open, setOpen] = React.useState(false);
    const selectedKey = props.value ? optionKey(props, props.value) : null;

    return (
        <Shell props={props}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={props.disabled}
                        aria-invalid={Boolean(props.error)}
                        className={cn(
                            'w-full justify-between font-normal',
                            props.size === 'small' && 'h-8'
                        )}
                    >
                        <span
                            className={cn(
                                'truncate',
                                !props.value && 'text-muted-foreground'
                            )}
                        >
                            {props.value
                                ? props.getOptionLabel(props.value)
                                : (props.placeholder ?? 'Select...')}
                        </span>
                        <ChevronsUpDown className="opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                >
                    <OptionList
                        props={props}
                        isSelected={(o) => optionKey(props, o) === selectedKey}
                        onSelect={(o) => {
                            props.onChange(
                                optionKey(props, o) === selectedKey ? null : o
                            );
                            setOpen(false);
                        }}
                    />
                </PopoverContent>
            </Popover>
        </Shell>
    );
}

export function MultiCombobox<T>(props: MultiComboboxProps<T>) {
    const [open, setOpen] = React.useState(false);
    const selectedKeys = new Set(props.value.map((v) => optionKey(props, v)));

    const toggle = (option: T) => {
        const key = optionKey(props, option);
        props.onChange(
            selectedKeys.has(key)
                ? props.value.filter((v) => optionKey(props, v) !== key)
                : [...props.value, option]
        );
    };

    return (
        <Shell props={props}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={props.disabled}
                        aria-invalid={Boolean(props.error)}
                        className="h-auto min-h-9 w-full justify-between font-normal"
                    >
                        <span className="flex flex-wrap gap-1">
                            {props.value.length === 0 && (
                                <span className="text-muted-foreground">
                                    {props.placeholder ?? 'Select...'}
                                </span>
                            )}
                            {props.value.map((option) => {
                                const key = optionKey(props, option);
                                const remove = () => toggle(option);
                                return (
                                    <React.Fragment key={key}>
                                        {props.renderChip ? (
                                            props.renderChip(option, remove)
                                        ) : (
                                            <Badge
                                                variant="secondary"
                                                className="gap-1"
                                            >
                                                {props.getOptionLabel(option)}
                                                <X
                                                    className="size-3 cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        remove();
                                                    }}
                                                />
                                            </Badge>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </span>
                        <ChevronsUpDown className="opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                >
                    <OptionList
                        props={props}
                        isSelected={(o) =>
                            selectedKeys.has(optionKey(props, o))
                        }
                        onSelect={toggle}
                    />
                </PopoverContent>
            </Popover>
        </Shell>
    );
}
